import { PARTNER_TERMS, type DuoInvite as DuoState } from '@trendator/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, moderationMessage } from '../api';
import { track } from '../analytics';
import { BackIcon, CameraIcon, CopyIcon, SendIcon } from '../components/icons';
import { rub } from '../format';
import { useBackButton } from '../hooks';
import { copyText, haptic, hapticResult, shareLink } from '../telegram';
import { Loading } from './Status';

const POLL_MS = 4000;

/** Приглашение по коду: своё — экран «Позови друга», чужое — «Сфоткаться вместе?». */
export function DuoScreen({
  code,
  onBack,
  onGeneration,
  onBalance,
}: {
  code: string;
  onBack: () => void;
  onGeneration: (id: string) => void;
  onBalance?: (balance: number) => void;
}) {
  const [invite, setInvite] = useState<DuoState | null>(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.duo(code);
      setInvite(d);
      onBalance?.(d.balance);
      return d;
    } catch {
      setMissing(true);
      return null;
    }
  }, [code, onBalance]);

  useEffect(() => {
    void load();
  }, [load]);

  // Пригласивший ждёт ответа друга — обновляем статус.
  useEffect(() => {
    if (!invite?.mine || invite.status !== 'pending') return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [invite?.mine, invite?.status, load]);

  if (missing) {
    return (
      <main className="screen screen--hero">
        <div className="center" style={{ minHeight: 'calc(80 * var(--dvh))' }}>
          <h1>Приглашение не найдено</h1>
          <p>Возможно, ссылка устарела.</p>
          <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={onBack}>
            На главную
          </button>
        </div>
      </main>
    );
  }
  if (!invite) return <Loading />;
  return invite.mine ? (
    <DuoInviteView invite={invite} setInvite={setInvite} onBack={onBack} onGeneration={onGeneration} />
  ) : (
    <DuoConfirmView invite={invite} setInvite={setInvite} onBack={onBack} onGeneration={onGeneration} />
  );
}

/** Позови друга — docs/design/DuoInvite.dc.html. */
function DuoInviteView({
  invite,
  setInvite,
  onBack,
  onGeneration,
}: {
  invite: DuoState;
  setInvite: (d: DuoState) => void;
  onBack: () => void;
  onGeneration: (id: string) => void;
}) {
  useBackButton(onBack);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const step = invite.status === 'accepted' ? 3 : 1;

  const cancel = async () => {
    setBusy(true);
    try {
      setInvite(await api.duoCancel(invite.code));
      hapticResult('success');
    } finally {
      setBusy(false);
    }
  };

  const closed = invite.status === 'declined' || invite.status === 'expired' || invite.status === 'canceled';
  const statusText =
    invite.status === 'accepted'
      ? `${invite.friendName ?? 'Друг'} согласился — уже делаем. Результат придёт вам обоим.`
      : invite.status === 'declined'
        ? `${invite.friendName ?? 'Друг'} отказался. ${rub(invite.price)} вернули на баланс.`
        : invite.status === 'expired'
          ? `Друг не ответил за сутки. ${rub(invite.price)} вернули на баланс.`
          : invite.status === 'canceled'
            ? `Приглашение отменено, ${rub(invite.price)} вернули на баланс.`
            : `Ждём ответа до ${new Date(invite.expiresAt).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}. ${rub(invite.price)} заморожено — вернём, если друг не согласится.`;

  return (
    <main className="screen duo">
      <header className="duo__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <span className="duo__crumb">{invite.trend.title} · дуо</span>
      </header>

      <div className="duo__pair" aria-hidden="true">
        <span className="duo__face duo__face--me" />
        <span className={`duo__face duo__face--friend${invite.status === 'accepted' ? ' duo__face--ok' : ''}`}>{invite.status === 'accepted' ? '✓' : '?'}</span>
      </div>

      <div className="duo__text">
        <h1>Позови друга в кадр</h1>
        <p>Его лицо попадёт в генерацию только после его согласия</p>
      </div>

      <ol className="glass-list duo__steps">
        {['Отправь ссылку другу', 'Он делает селфи и жмёт «Согласен»', 'Вы оба получаете результат в бот'].map((t, i) => (
          <li key={t} className={i < step ? 'on' : ''}>
            <span>{i + 1}</span>
            {t}
          </li>
        ))}
      </ol>

      <p className={`duo__status${closed ? ' duo__status--closed' : ''}`} role="status">
        {statusText}
      </p>

      <div className="duo__gift">
        <span aria-hidden="true">🎁</span>
        <span>
          Друг новенький? Ему первая генерация бесплатно, тебе — {PARTNER_TERMS.firstPercent}% с его первой покупки
        </span>
      </div>

      <div className="duo__footer">
        {invite.status === 'accepted' && invite.generationId ? (
          <button type="button" className="btn-accent" onClick={() => onGeneration(invite.generationId!)}>
            Смотреть, как рисуем
          </button>
        ) : invite.status === 'pending' && invite.link ? (
          <div className="duo__actions">
            <button
              type="button"
              className="btn-accent"
              onClick={() => {
                haptic();
                shareLink(invite.link!, invite.shareText);
                track('share', { what: 'duo' });
              }}
            >
              <SendIcon />
              Отправить в Telegram
            </button>
            <button
              type="button"
              className="vres__round"
              aria-label="Скопировать ссылку"
              onClick={async () => {
                if (await copyText(invite.link!)) {
                  haptic('select');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                }
              }}
            >
              <CopyIcon />
            </button>
          </div>
        ) : null}
        {copied ? <p className="duo__hint">Ссылка скопирована</p> : null}
        {invite.status === 'pending' ? (
          <button type="button" className="link-btn duo__cancel" disabled={busy} onClick={() => void cancel()}>
            Отменить приглашение
          </button>
        ) : null}
      </div>
    </main>
  );
}

/** Друга позвали — docs/design/DuoConfirm.dc.html. */
function DuoConfirmView({
  invite,
  setInvite,
  onBack,
  onGeneration,
}: {
  invite: DuoState;
  setInvite: (d: DuoState) => void;
  onBack: () => void;
  onGeneration: (id: string) => void;
}) {
  useBackButton(onBack);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const noun = invite.trend.output === 'video' ? 'видео' : invite.trend.output === 'stickers' ? 'стикеров' : 'фото';

  const accept = async (file: File | undefined) => {
    if (!file) return;
    setBusy('accept');
    setError(null);
    try {
      const d = await api.duoAccept(invite.code, file);
      hapticResult('success');
      setInvite(d);
      if (d.generationId) onGeneration(d.generationId);
    } catch (err) {
      hapticResult('error');
      const code = err instanceof ApiError ? err.code : '';
      setError(
        moderationMessage(err) ??
          (code === 'bad_selfie'
          ? `${String((err as ApiError).data.message ?? 'Не видно лица')} — переснимем?`
          : code === 'not_pending'
            ? 'На приглашение уже ответили.'
            : code === 'too_many_attempts'
              ? 'Слишком много попыток — попробуй через час.'
              : code === 'duo_disabled'
                ? 'Дуо ещё подключается — загляни чуть позже.'
                : 'Не получилось — попробуй ещё раз.'),
      );
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const decline = async () => {
    setBusy('decline');
    try {
      setInvite(await api.duoDecline(invite.code));
    } catch {
      setError('Не получилось — попробуй ещё раз.');
    } finally {
      setBusy(null);
    }
  };

  if (invite.status !== 'pending') {
    const text =
      invite.status === 'accepted'
        ? 'Ты в кадре! Результат придёт тебе в бот.'
        : invite.status === 'declined'
          ? 'Ок, без тебя. Мы сообщили другу.'
          : 'Приглашение уже не действует.';
    return (
      <main className="screen screen--hero">
        <div className="center" style={{ minHeight: 'calc(80 * var(--dvh))' }}>
          <h1>{invite.trend.title}</h1>
          <p>{text}</p>
          {invite.status === 'accepted' && invite.generationId ? (
            <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={() => onGeneration(invite.generationId!)}>
              Смотреть
            </button>
          ) : (
            <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={onBack}>
              Посмотреть тренды
            </button>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="screen duo duo--confirm">
      <div
        className="duo__example"
        style={
          invite.trend.coverUrl
            ? { backgroundImage: `url("${invite.trend.coverUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: invite.trend.coverGradient }
        }
      >
        <span className="duo__example-chip">Пример результата</span>
        <div className="duo__example-who">
          <span className="duo__face duo__face--sm" aria-hidden="true" />
          <span className="trend__face-text">
            <span className="duo__who-name">{invite.inviterName} зовёт тебя в тренд</span>
            <span className="duo__who-sub">
              «{invite.trend.title}» · {invite.trend.frames} {noun}
            </span>
          </span>
        </div>
      </div>

      <div className="duo__text duo__text--left">
        <h1>Сфоткаемся вместе?</h1>
        <p>Платит {invite.inviterName}. Твоё селфи используем только в этой генерации и удалим через 24 часа.</p>
      </div>

      {invite.friendFreeGeneration ? (
        <div className="duo__gift">
          <span aria-hidden="true">🎁</span>
          <span>Плюс тебе одна своя генерация бесплатно</span>
        </div>
      ) : null}
      {error ? (
        <p className="error-text duo__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="duo__footer">
        <button type="button" className="btn-accent" disabled={Boolean(busy)} onClick={() => fileRef.current?.click()}>
          <CameraIcon />
          {busy === 'accept' ? 'Проверяем селфи…' : 'Селфи и согласен'}
        </button>
        <button type="button" className="duo__no" disabled={Boolean(busy)} onClick={() => void decline()}>
          Не хочу
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => void accept(e.target.files?.[0])} />
      </div>
    </main>
  );
}
